import * as React from 'react'
import { ReactElement, useEffect, useMemo, useState } from 'react'
import useUsers from '../../../hooks/useUsers'
import { parseNumber } from '../../../helpers/numberHelper'
import { Field } from '@fluentui/react-components'
import MoneyInput from '../../Input/MoneyInput'
import { InsolvencyModuleDto } from '../../../hooks/module/useFileModule'
import { UserBaseTierFormContainerProps } from './UserTierFormContainer'
import { InsolvencyTierDto, isInsolvencyTier } from '../../../config/finance/financeConfig'
import { useSettings } from '../../../hooks/useSettings'
import Loading from '../../../components/Loading'
import FormattableNumberInput, { CommitEventType } from '../../Input/FormattableNumberInput'
import { usePrevious } from '@fluentui/react-hooks'
import { UserTierAction } from './UserTierList'
import UserPicker from '../../TagPicker/UserPicker'
import { useTranslation } from 'react-i18next'
import EnumMapCombobox from '../../Combobox/EnumMapCombobox'

type UserInsolvencyTierFormContainerProps = UserBaseTierFormContainerProps<InsolvencyTierDto> & {
    insolvencyModule: InsolvencyModuleDto
}

export default function UserInsolvencyTierFormContainer(props: UserInsolvencyTierFormContainerProps): ReactElement {
    const { t: translate } = useTranslation()
    const { values, setFieldValue } = props.fileContext
    const tier = useMemo(() => (values.tiers[props.index] as InsolvencyTierDto) ?? props.initialValues, [values.tiers])
    const prevTierValues = usePrevious(tier)
    const settings = useSettings()
    const { users } = useUsers()

    const [selectedUsers, setSelectedUsers] = useState<string[]>([tier?.userId])
    const [selectedRoles, setSelectedRoles] = useState<string[]>([tier?.role])

    const insolvencyRoles = useMemo<Map<string, string>>(
        () => new Map(settings?.insolvency?.roles.map(x => [x.key, x.name] as [string, string])),
        [settings?.insolvency],
    )

    useEffect(() => {
        const userTier = users
            .find(user => user.id === tier?.userId)
            ?.tiers?.filter(tier => isInsolvencyTier(tier))
            .map(tier => tier as InsolvencyTierDto)
            .find(tier => tier?.currency === values.currency)

        if (
            (userTier && prevTierValues && tier?.userId !== prevTierValues.userId) ||
            (prevTierValues === undefined && props.action === UserTierAction.ADD)
        ) {
            setFieldValue(`tiers[${props.index}].role`, userTier?.role ?? tier?.role)
            setSelectedRoles([tier.role])

            setFieldValue(`tiers[${props.index}].estateRate`, userTier?.estateRate ?? tier?.estateRate)
            setFieldValue(
                `tiers[${props.index}].factorExperience`,
                userTier?.factorExperience ?? tier?.factorExperience,
            )
            setFieldValue(`tiers[${props.index}].factorEstate`, userTier?.factorEstate ?? tier?.factorEstate)
            setFieldValue(`tiers[${props.index}].factorTotal`, userTier?.factorTotal ?? tier?.factorTotal)
        }
    }, [tier?.userId])

    useEffect(() => {
        if (tier?.factorTotal && tier?.estateRate) {
            const value = parseNumber(tier.factorTotal, 'en') * parseNumber(tier.estateRate, 'en')
            setFieldValue(`tiers[${props.index}].salesRate`, value.toString())
        }
    }, [tier?.factorTotal, tier?.estateRate])

    useEffect(() => {
        if (tier?.factorExperience && tier?.factorEstate) {
            const value =
                Math.ceil(parseNumber(tier.factorExperience, 'en') * parseNumber(tier.factorEstate, 'en') * 10) / 10
            setFieldValue(`tiers[${props.index}].factorTotal`, value.toString())
        }
    }, [tier?.factorExperience, tier?.factorEstate])

    if (!tier) {
        return <Loading />
    }

    return (
        <>
            <Field label={translate('file.edit.form.finance.rates.tier.userId')} required>
                <UserPicker
                    singleSelect
                    selectedOptions={selectedUsers}
                    onOptionsSelect={(_, selectedOptions) => {
                        setSelectedUsers(selectedOptions)
                        setFieldValue(`tiers[${props.index}].userId`, selectedOptions?.[0])
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.role')} required>
                <EnumMapCombobox<string>
                    values={insolvencyRoles}
                    selectedOptions={selectedRoles}
                    onOptionSelect={(_, data) => {
                        setFieldValue(`tiers[${props.index}].role`, data.optionValue)
                        setSelectedRoles(data.selectedOptions)
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.factorExperience')}>
                <FormattableNumberInput
                    allowNegative={false}
                    decimals={1}
                    value={tier?.factorExperience}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`tiers[${props.index}].factorExperience`, value)
                        }
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.factorEstate')}>
                <FormattableNumberInput
                    allowNegative={false}
                    decimals={1}
                    value={tier?.factorEstate}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`tiers[${props.index}].factorEstate`, value)
                        }
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.factorTotal')}>
                <FormattableNumberInput
                    allowNegative={false}
                    decimals={1}
                    value={tier?.factorTotal}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`tiers[${props.index}].factorTotal`, value)
                        }
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.estateRate')}>
                <MoneyInput
                    value={tier.estateRate}
                    currency={tier?.currency}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`tiers[${props.index}].estateRate`, value)
                        }
                    }}
                />
            </Field>
            <Field label={translate('file.edit.form.finance.rates.tier.salesRate')}>
                <MoneyInput
                    value={tier?.salesRate}
                    currency={tier?.currency}
                    onCommit={(eventType: CommitEventType, value: string) => {
                        if (eventType !== CommitEventType.OnLoad) {
                            setFieldValue(`tiers[${props.index}].salesRate`, value)
                        }
                    }}
                />
            </Field>
        </>
    )
}
